import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, GraduationCap, Brain, ArrowRight, Check } from "lucide-react";

export default function HosysAILanding() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white overflow-x-hidden" data-testid="hosysai-landing">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-fuchsia-500/40">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-bold text-lg leading-none">HOSYS<span className="text-cyan-300"> AI</span></p>
            <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-0.5">Academy & Compliance</p>
          </div>
        </div>
        <Link
          to="/login"
          className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-200 hover:bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
          data-testid="header-login"
        >
          Accedir
        </Link>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pt-16 pb-20 text-center">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/30 text-xs font-semibold text-cyan-300 uppercase tracking-widest">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
          AI Act obligatori · Feb 2026
        </span>
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1]">
          Formació, governança i compliment<br />
          en <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 to-fuchsia-400">Intel·ligència Artificial</span>
        </h1>
        <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
          Gestiona la formació, el compliment normatiu, la governança i les evidències relacionades amb l'ús de la IA a la teva organització.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/login"
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white font-semibold shadow-lg shadow-fuchsia-500/30 hover:shadow-fuchsia-500/50 transition-all flex items-center gap-2"
            data-testid="cta-login"
          >
            Accedir <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/register"
            className="px-6 py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white font-semibold transition-all"
            data-testid="cta-register"
          >
            Crear empresa
          </Link>
          <a
            href="mailto:hola@ho-sys.com?subject=HOSYS%20AI%20-%20Sol·licitud%20d'informació"
            className="px-6 py-3 rounded-xl text-slate-300 hover:text-white font-medium transition-all"
            data-testid="cta-info"
          >
            Sol·licitar informació →
          </a>
        </div>
      </section>

      {/* Feature grid */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-4">
          {[
            {
              icon: Brain,
              color: "cyan",
              title: "Inventari d'IA",
              desc: "Registra cada eina d'IA i classifica-la automàticament segons el seu nivell de risc (baix, moderat, requereix revisió, alt).",
            },
            {
              icon: GraduationCap,
              color: "fuchsia",
              title: "AI Academy",
              desc: "Cursos, itineraris formatius, exàmens i certificats per als teus treballadors segons rol i responsabilitats.",
            },
            {
              icon: ShieldCheck,
              color: "emerald",
              title: "Compliance",
              desc: "Diagnòstic inicial, política interna generada per IA, acceptació digital dels treballadors i expedient d'evidències.",
            },
          ].map((f) => (
            <div key={f.title} className="p-6 rounded-2xl border border-white/10 bg-white/5 hover:border-white/20 transition-colors">
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-${f.color}-400/20 to-${f.color}-500/5 border border-${f.color}-400/30 mb-4`}>
                <f.icon className={`w-5 h-5 text-${f.color}-300`} />
              </div>
              <p className="font-semibold text-white text-lg">{f.title}</p>
              <p className="text-sm text-slate-400 mt-2">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 pb-20">
        <div className="p-8 rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-800/40">
          <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold mb-2">Perfect per a</p>
          <h2 className="text-2xl font-bold mb-6">Empreses, gestories i associacions professionals</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              "Compliment RD-Llei / AI Act",
              "Trazabilitat i evidències",
              "Formació personalitzada per rol",
              "Assistent IA per polítiques",
              "Alertes proactives",
              "Multi-empresa (gestories)",
            ].map((b) => (
              <div key={b} className="flex items-center gap-2 text-slate-200">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span className="text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-6 px-6 text-center text-xs text-slate-500">
        HOSYS AI · Powered by <a href="https://hosys.es" className="text-cyan-400 hover:text-cyan-300">Ho-Sys</a> · La classificació d'IA és orientativa i no substitueix una avaluació jurídica.
      </footer>
    </div>
  );
}
